// admin/src/api/adminApiClient.js - Admin API 클라이언트
import axios from 'axios';
import { getAuth } from 'firebase/auth';
import { app } from '../firebase/firebaseConfig.jsx';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';

// Admin API 클라이언트 생성
export const adminApiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  timeout: 15000,
});

// 요청 인터셉터: Firebase Auth 토큰 추가
adminApiClient.interceptors.request.use(async (config) => {
  const auth = getAuth(app);
  const user = auth.currentUser;

  if (user) {
    try {
      const idToken = await user.getIdToken(true);
      config.headers.Authorization = `Bearer ${idToken}`;
    } catch (error) {
      console.error('Error getting Firebase ID token:', error);
    }
  }

  console.log(`🌐 Admin API 요청: ${config.method?.toUpperCase()} ${config.url}`);
  return config;
}, (error) => {
  console.error('❌ Admin API 요청 인터셉터 오류:', error);
  return Promise.reject(error);
});

// 응답 인터셉터: 에러 처리
adminApiClient.interceptors.response.use(
  (response) => {
    console.log(`✅ Admin API 응답 성공: ${response.config.method?.toUpperCase()} ${response.config.url}`);
    return response;
  },
  async (error) => {
    console.error(`❌ Admin API 요청 실패: ${error.config?.method?.toUpperCase()} ${error.config?.url}`, error);

    if (error.response) {
      const { status, data } = error.response;
      console.error('❌ Admin API 응답 에러:', status, data);

      if (status === 401) {
        console.warn('⚠️ 인증 토큰 만료 또는 유효하지 않음. 재로그인 필요.');
        window.dispatchEvent(new CustomEvent('token-expired'));
        throw new Error('Session expired. Please log in again.');
      }

      if (status === 403) {
        throw new Error('Admin privileges required.');
      }

      throw new Error(data.message || data.statusText || `API Error: ${status}`);
    } else if (error.request) {
      console.error('❌ 네트워크 요청 실패 (응답 없음):', error.request);
      throw new Error('Network request failed. No response received.');
    } else {
      console.error('❌ 기타 클라이언트 측 에러:', error.message);
      throw new Error(`Client-side error: ${error.message}`);
    }
  }
);

// 통합 API 함수 정의
const adminApi = {
  // 사용자 관리
  getUsers: (params) => adminApiClient.get('/api/users', { params }),
  getUserProfile: (userId) => adminApiClient.get(`/api/users/${userId}`),
  updateUserProfile: (userId, profileData) => adminApiClient.put(`/api/users/${userId}`, profileData),
  updateUserKYC: (userId, kycData) => adminApiClient.patch(`/api/users/${userId}/kyc`, kycData),

  // 송금 관리
  getRemittances: (params) => adminApiClient.get('/api/remittances', { params }),
  getRemittanceDetail: (remitId) => adminApiClient.get(`/api/remittances/${remitId}`),

  // KYC → Hecto sync retry (Model B: after user submit; does not approve)
  syncKycToHecto: (payload) =>
    adminApiClient.post('/api/kyc/admin/sync-hecto', payload),
  ensureKycHectoSync: (payload) =>
    adminApiClient.post('/api/kyc/admin/ensure-hecto-sync', payload),
  settleRemittance: (remitId) => adminApiClient.patch(`/api/remittances/${remitId}/settle`),

  // 금융 설정
  getFinanceConfig: () => adminApiClient.get('/api/finance/config'),
  updateFinanceConfig: (configData) => adminApiClient.put('/api/finance/config', configData),
  
  // 송금업체 계좌 관리
  getProviderAccounts: () => adminApiClient.get('/api/finance/provider-accounts'),
  createProviderAccount: (accountData) => adminApiClient.post('/api/finance/provider-accounts', accountData),
  updateProviderAccount: (accountId, accountData) => adminApiClient.put(`/api/finance/provider-accounts/${accountId}`, accountData),
  deleteProviderAccount: (accountId) => adminApiClient.delete(`/api/finance/provider-accounts/${accountId}`),

  // 파일 관리
  getFileList: (params) => adminApiClient.get('/api/files/list', { params }),
  downloadFile: (storagePath) => adminApiClient.get(`/api/files/download/${storagePath}`),
  deleteFile: (storagePath) => adminApiClient.delete(`/api/files/delete/${storagePath}`),

  // 알림 관리
  sendNotification: (notificationData) => adminApiClient.post('/api/notifications', notificationData),
  getNotifications: (params) => adminApiClient.get('/api/notifications', { params }),
};

export default adminApi;